import { ApprovalStatus, CompletionCondition } from '@insio/shared-types';
import type { BadgeProps } from '@insio/ui-toolkit';
import { AwaitingCompletionStatus, awaitingCompletionStatus } from './awaiting-completion-status';
import { CANCELLED_BADGE_COLOR, COMPLETED_BADGE_COLOR } from './procurement-status';

/**
 * The status an order shows: what it waits for, not what happened. Derived from `approvalStatus`,
 * the dispatch, `isCancelled`, `isCompleted` and `unmetCompletionConditions`.
 */
export const PurchaseOrderDisplayStatus = {
  DRAFT: 'DRAFT',
  FOR_APPROVAL: 'FOR_APPROVAL',
  /** Approved, not sent to the supplier yet, and the account requires sending. */
  TO_SEND: 'TO_SEND',
  /** Sent, or approved where sending is not required: what it still waits for, as a requisition. */
  ...AwaitingCompletionStatus,
  REJECTED: 'REJECTED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
} as const;

export type PurchaseOrderDisplayStatus =
  (typeof PurchaseOrderDisplayStatus)[keyof typeof PurchaseOrderDisplayStatus];

/** Minimal order shape needed to derive the displayed status. */
export interface PurchaseOrderDisplayStatusSource {
  readonly approvalStatus: ApprovalStatus;
  /** When it was sent to the supplier; null or left out until then. */
  readonly dispatchedAt?: unknown;
  /** Sent to the supplier, for a shape that carries no `dispatchedAt`; wins over it when set. */
  readonly isDispatched?: boolean;
  readonly isCancelled: boolean;
  readonly isCompleted: boolean;
  /** Left out (a shape without it): no named condition, and an unsent order waits for sending. */
  readonly unmetCompletionConditions?: readonly CompletionCondition[] | null;
}

/**
 * Whether an approved order still waits to be sent: unsent, and sending is a condition the
 * account requires. A shape without the conditions keeps the old rule — unsent is to send.
 */
export function isAwaitingDispatch(
  source: Pick<
    PurchaseOrderDisplayStatusSource,
    'dispatchedAt' | 'isDispatched' | 'unmetCompletionConditions'
  >
): boolean {
  const isDispatched = source.isDispatched ?? source.dispatchedAt != null;
  if (isDispatched) return false;
  return (
    source.unmetCompletionConditions == null ||
    source.unmetCompletionConditions.includes(CompletionCondition.STATUS_NOT_REACHED)
  );
}

/** What an order shows before approval; the automation and a user deciding look alike. */
const SAME_NAMED: ReadonlyMap<string, PurchaseOrderDisplayStatus> = new Map([
  [ApprovalStatus.UNPROCESSED, PurchaseOrderDisplayStatus.DRAFT],
  [ApprovalStatus.PENDING, PurchaseOrderDisplayStatus.FOR_APPROVAL],
  [ApprovalStatus.FOR_REVIEW, PurchaseOrderDisplayStatus.FOR_APPROVAL],
  [ApprovalStatus.REJECTED, PurchaseOrderDisplayStatus.REJECTED]
]);

/** Derive the one status an order shows: cancelled › completed › what it waits for. */
export function purchaseOrderDisplayStatus(
  source: PurchaseOrderDisplayStatusSource
): PurchaseOrderDisplayStatus {
  if (source.isCancelled) return PurchaseOrderDisplayStatus.CANCELLED;
  if (source.isCompleted) return PurchaseOrderDisplayStatus.COMPLETED;
  if (source.approvalStatus === ApprovalStatus.APPROVED) {
    return isAwaitingDispatch(source)
      ? PurchaseOrderDisplayStatus.TO_SEND
      : awaitingCompletionStatus(source.unmetCompletionConditions);
  }
  // A status the map does not know — an older row, a newer server — is something nobody has
  // finished with, so it reads as work in hand rather than as an empty badge.
  return SAME_NAMED.get(source.approvalStatus) ?? PurchaseOrderDisplayStatus.DRAFT;
}

/** Badge colour for a displayed status — one map, so an order reads the same wherever it shows. */
export const purchaseOrderDisplayStatusColor = (
  status: PurchaseOrderDisplayStatus
): NonNullable<BadgeProps['color']> => DISPLAY_STATUS_COLOR[status] ?? 'neutral';

const DISPLAY_STATUS_COLOR: Readonly<
  Record<PurchaseOrderDisplayStatus, NonNullable<BadgeProps['color']>>
> = {
  [PurchaseOrderDisplayStatus.DRAFT]: 'neutral',
  [PurchaseOrderDisplayStatus.FOR_APPROVAL]: 'warning',
  [PurchaseOrderDisplayStatus.TO_SEND]: 'success',
  // Sent and under way, the colour a dispatched order has always worn.
  [PurchaseOrderDisplayStatus.AWAITING_DELIVERY]: 'info',
  [PurchaseOrderDisplayStatus.AWAITING_INVOICE]: 'info',
  [PurchaseOrderDisplayStatus.AWAITING_DELIVERY_AND_INVOICE]: 'info',
  [PurchaseOrderDisplayStatus.AWAITING_COMPLETION]: 'info',
  [PurchaseOrderDisplayStatus.REJECTED]: 'error',
  [PurchaseOrderDisplayStatus.COMPLETED]: COMPLETED_BADGE_COLOR,
  [PurchaseOrderDisplayStatus.CANCELLED]: CANCELLED_BADGE_COLOR
};
